// ------------------------------------------------------------
// High-frequency live frame state (outside Zustand)
// ------------------------------------------------------------

const raf =
  typeof globalThis.requestAnimationFrame === 'function'
    ? globalThis.requestAnimationFrame.bind(globalThis)
    : (cb: FrameRequestCallback) => setTimeout(() => cb(performance.now()), 16);

const cancelRaf =
  typeof globalThis.cancelAnimationFrame === 'function'
    ? globalThis.cancelAnimationFrame.bind(globalThis)
    : (id: number) => clearTimeout(id);

export interface ProductVisualStyle {
  style_id?: string;
  source_input_id?: string;
  product_type_id: string;
  color?: string;
  shape?: string;
  size_px?: number;
}

export interface LiveProductSprite {
  product_id: number;
  product_type_id: string;
  visual_style_id?: string;
  parent_product_id?: number;
  route_id?: string;
  processor_id?: string;
  progress?: number;
  /** Resolved draw size from server (preferred over catalog base size). */
  size_px?: number;
}

export interface LiveFrame {
  frame_seq: number;
  sim_time: number;
  wall_timestamp: number;
  gate_states: Record<string, string>;
  products: LiveProductSprite[];
  route_occupancy: Record<string, number>;
  processor_queue_lengths: Record<string, number>;
  processor_flash: Record<string, number>;
  output_flash: Record<string, number>;
  in_system_count: number;
  output_totals: Record<string, number>;
  total_output: number;
  total_generated: number;
  total_product_count?: number;
  visualized_product_count?: number;
  generated_by_product_type?: Record<string, number>;
  active_by_product_type?: Record<string, number>;
  streamed_by_product_type?: Record<string, number>;
}

export interface InterpolatedSprite extends LiveProductSprite {
  interpolated_progress: number;
}

type FrameListener = () => void;

import { resolveProductVisual } from './productVisualGeometry';
import { DEFAULT_VISUAL_FPS, visualFpsToIntervalMs } from './simulationDuration';

const FPS_WINDOW_MS = 1000;
const DEFAULT_SNAPSHOT_BLEND_MS = 100;
const MAX_EXTRAPOLATION_RATIO = 0.5;

let visualStyles = new Map<string, ProductVisualStyle>();
let previousFrame: LiveFrame | null = null;
let targetFrame: LiveFrame | null = null;
let lastAcceptedFrameSeq = 0;
let frameBlendStart = 0;
let frameBlendDurationMs = DEFAULT_SNAPSHOT_BLEND_MS;
let targetVisualFps = DEFAULT_VISUAL_FPS;
let lastDrawAt = 0;
let rafId: number | null = null;
const listeners = new Set<FrameListener>();

let receivedFrameCount = 0;
let receivedFpsWindowStart = 0;
let receivedFpsEstimate = 0;
let renderedFrameCount = 0;
let renderedFpsWindowStart = 0;
let renderedFpsEstimate = 0;
let droppedSnapshotCount = 0;
let renderedByProductType: Record<string, number> = {};

function styleKey(style: ProductVisualStyle): string {
  return style.style_id ?? style.product_type_id;
}

function updateFpsEstimate(
  count: number,
  windowStart: number,
  setter: (value: number) => void,
): { count: number; windowStart: number } {
  const now = performance.now();
  if (windowStart <= 0) {
    return { count: 1, windowStart: now };
  }
  const elapsed = now - windowStart;
  if (elapsed >= FPS_WINDOW_MS) {
    setter((count * 1000) / elapsed);
    return { count: 1, windowStart: now };
  }
  return { count: count + 1, windowStart };
}

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

function tick() {
  if (targetFrame) {
    const now = performance.now();
    const minDrawInterval = visualFpsToIntervalMs(targetVisualFps);
    if (now - lastDrawAt >= minDrawInterval) {
      lastDrawAt = now;
      const stats = updateFpsEstimate(
        renderedFrameCount,
        renderedFpsWindowStart,
        (value) => {
          renderedFpsEstimate = value;
        },
      );
      renderedFrameCount = stats.count;
      renderedFpsWindowStart = stats.windowStart;
      notify();
    }
  }
  rafId = raf(tick);
}

export function startLiveFrameAnimation() {
  if (rafId == null) {
    rafId = raf(tick);
  }
}

export function stopLiveFrameAnimation() {
  if (rafId != null) {
    cancelRaf(rafId);
    rafId = null;
  }
}

export function subscribeLiveFrames(listener: FrameListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setTargetVisualFps(fps: number) {
  targetVisualFps = fps;
}

export function getTargetVisualFps(): number {
  return targetVisualFps;
}

export function getDroppedSnapshotCount(): number {
  return droppedSnapshotCount;
}

export function getVisualFpsDiagnostics(): {
  requestedFps: number;
  receivedFps: number;
  renderedFps: number;
  droppedSnapshots: number;
} {
  return {
    requestedFps: targetVisualFps,
    receivedFps: receivedFpsEstimate,
    renderedFps: renderedFpsEstimate,
    droppedSnapshots: droppedSnapshotCount,
  };
}

export function getProductMixDiagnostics(): {
  generatedByProductType: Record<string, number>;
  activeByProductType: Record<string, number>;
  streamedByProductType: Record<string, number>;
  renderedByProductType: Record<string, number>;
} {
  return {
    generatedByProductType: targetFrame?.generated_by_product_type ?? {},
    activeByProductType: targetFrame?.active_by_product_type ?? {},
    streamedByProductType: targetFrame?.streamed_by_product_type ?? {},
    renderedByProductType: { ...renderedByProductType },
  };
}

export function setRenderedByProductType(counts: Record<string, number>) {
  renderedByProductType = counts;
}

export function setVisualCatalog(styles: ProductVisualStyle[]) {
  visualStyles = new Map(
    styles.map((s) => {
      const resolved = resolveProductVisual(s);
      const key = styleKey(s);
      return [
        key,
        {
          style_id: key,
          source_input_id: s.source_input_id,
          product_type_id: s.product_type_id,
          color: resolved.color,
          shape: resolved.shape,
          size_px: resolved.size_px,
        },
      ];
    }),
  );
}

/** Merge document-derived styles; server catalog entries win on key collision. */
export function mergeVisualCatalog(styles: ProductVisualStyle[]) {
  const merged = new Map(visualStyles);
  for (const style of styles) {
    const resolved = resolveProductVisual(style);
    const key = styleKey(style);
    if (merged.has(key)) continue;
    merged.set(key, {
      style_id: key,
      source_input_id: style.source_input_id,
      product_type_id: style.product_type_id,
      color: resolved.color,
      shape: resolved.shape,
      size_px: resolved.size_px,
    });
  }
  visualStyles = merged;
}

export function getVisualStyle(styleId: string): ProductVisualStyle | undefined {
  const style = visualStyles.get(styleId);
  if (!style) return undefined;
  const resolved = resolveProductVisual(style);
  return {
    style_id: styleId,
    source_input_id: style.source_input_id,
    product_type_id: style.product_type_id,
    color: resolved.color,
    shape: resolved.shape,
    size_px: resolved.size_px,
  };
}

export function getVisualCatalog(): Map<string, ProductVisualStyle> {
  return visualStyles;
}

export function resetLiveFrameState() {
  previousFrame = null;
  targetFrame = null;
  lastAcceptedFrameSeq = 0;
  frameBlendStart = 0;
  lastDrawAt = 0;
  visualStyles = new Map();
  receivedFrameCount = 0;
  receivedFpsWindowStart = 0;
  receivedFpsEstimate = 0;
  renderedFrameCount = 0;
  renderedFpsWindowStart = 0;
  renderedFpsEstimate = 0;
  droppedSnapshotCount = 0;
  renderedByProductType = {};
  stopLiveFrameAnimation();
}

export function pushLiveFrame(frame: LiveFrame, blendMs = DEFAULT_SNAPSHOT_BLEND_MS) {
  if (frame.frame_seq > 0) {
    if (frame.frame_seq <= lastAcceptedFrameSeq) {
      return;
    }
    if (frame.frame_seq > lastAcceptedFrameSeq + 1) {
      droppedSnapshotCount += frame.frame_seq - lastAcceptedFrameSeq - 1;
    }
    lastAcceptedFrameSeq = frame.frame_seq;
  }

  const recvStats = updateFpsEstimate(
    receivedFrameCount,
    receivedFpsWindowStart,
    (value) => {
      receivedFpsEstimate = value;
    },
  );
  receivedFrameCount = recvStats.count;
  receivedFpsWindowStart = recvStats.windowStart;

  previousFrame = targetFrame ?? frame;
  targetFrame = frame;
  frameBlendStart = performance.now();
  frameBlendDurationMs = Math.max(16, blendMs);
  startLiveFrameAnimation();
}

export function getTargetLiveFrame(): LiveFrame | null {
  return targetFrame;
}

export function getVisualizationCounts(): { visualized: number; total: number } | null {
  if (!targetFrame) return null;
  const total = targetFrame.total_product_count ?? targetFrame.products.length;
  const visualized =
    targetFrame.visualized_product_count ?? targetFrame.products.length;
  return { visualized, total };
}

function interpolateRouteProgress(
  sprite: LiveProductSprite,
  prev: LiveProductSprite | undefined,
  blend: number,
  ease: number,
): { route_id?: string; progress: number } {
  const fromProgress = prev?.progress ?? sprite.progress ?? 0;
  const toProgress = sprite.progress ?? fromProgress;
  const prevRoute = prev?.route_id;
  const nextRoute = sprite.route_id;

  if (prevRoute && nextRoute && prevRoute !== nextRoute) {
    if (blend < 0.5) {
      const phase = blend * 2;
      return {
        route_id: prevRoute,
        progress: Math.min(1, fromProgress + (1 - fromProgress) * phase),
      };
    }
    const phase = (blend - 0.5) * 2;
    return {
      route_id: nextRoute,
      progress: Math.min(1, toProgress * phase),
    };
  }

  return {
    route_id: nextRoute,
    progress: Math.min(1, fromProgress + (toProgress - fromProgress) * ease),
  };
}

export function getInterpolatedSprites(): InterpolatedSprite[] {
  if (!targetFrame) return [];

  const now = performance.now();
  const elapsed = now - frameBlendStart;
  const blend = Math.min(1, elapsed / frameBlendDurationMs);
  const extrapolationMs = Math.max(0, elapsed - frameBlendDurationMs);
  const maxExtrapolationMs = Math.max(
    16,
    frameBlendDurationMs * MAX_EXTRAPOLATION_RATIO,
  );
  const extrapolationFactor = Math.min(1, extrapolationMs / maxExtrapolationMs);
  const ease = blend < 1 ? blend : 1 + extrapolationFactor * 0.25;

  const prevById = new Map<number, LiveProductSprite>();
  if (previousFrame) {
    for (const sprite of previousFrame.products) {
      prevById.set(sprite.product_id, sprite);
    }
  }

  return targetFrame.products.map((sprite) => {
    const prev = prevById.get(sprite.product_id);
    const routeProgress = interpolateRouteProgress(sprite, prev, blend, ease);
    return {
      ...sprite,
      route_id: routeProgress.route_id,
      interpolated_progress: routeProgress.progress,
    };
  });
}

export function getLiveOverlayMetrics(): Omit<
  LiveFrame,
  'products' | 'sim_time' | 'frame_seq' | 'wall_timestamp' | 'gate_states'
> | null {
  if (!targetFrame) return null;
  const {
    route_occupancy,
    processor_queue_lengths,
    processor_flash,
    output_flash,
    in_system_count,
    output_totals,
    total_output,
    total_generated,
    total_product_count,
    visualized_product_count,
    generated_by_product_type,
    active_by_product_type,
    streamed_by_product_type,
  } = targetFrame;
  return {
    route_occupancy,
    processor_queue_lengths,
    processor_flash,
    output_flash,
    in_system_count,
    output_totals,
    total_output,
    total_generated,
    total_product_count,
    visualized_product_count,
    generated_by_product_type,
    active_by_product_type,
    streamed_by_product_type,
  };
}
